import React, {useEffect, useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {TaskSetDrawer} from './TaskSetDrawer';
import {TaskSetListPane, TaskSetRow} from './TaskSetListPane';
import {TaskSetNameChip} from './TaskSetNameChip';
import {CompanyWorkingHours} from './CreateTaskSetDrawer';
import {TaskSetEditForm} from './TaskSetEditForm';
import {useDeleteTaskSet} from './useDeleteTaskSet';

/**
 * Matches the drawer's close button so the two header controls read as a pair.
 */
const BackButton = styled.button`
	border: 0;
	background: transparent;
	padding: 4px;
	cursor: pointer;
	color: #7d7d7d;
	line-height: 0;

	&:focus-visible {
		outline: 2px solid #6e0fea;
		outline-offset: 2px;
	}
`;

export interface ManageTaskSetsDrawerProps {
	isOpen: boolean;
	project: unknown;
	/**
	 * The set to open. The chip on a task row supplies it, and naming a set means
	 * opening its form -- from an instance the reader has already chosen which set
	 * they mean, so the list in between is a step that answers nothing. Absent (the
	 * toolbar button) opens the list instead.
	 *
	 * It also survives the trip: Back from that form lands on the list with this
	 * row picked out, so the reader can see where they came from.
	 */
	highlightSetId?: string | null;
	/**
	 * Bumped by the caller on every open request. Without it, asking twice in a
	 * row changes no prop and fires no effect. Same idiom as the create drawer's
	 * own open id.
	 */
	openNonce?: number;
	onClose: () => void;
	/** Refetches the host after a delete or an edit, which move many rows at once. */
	onDeleted?: () => void;
	onEdited?: (instancesUpdated: number) => void;
	/**
	 * Everything the config pane's edit form offers, plus the two permissions its
	 * actions answer to. Passed through rather than queried here: both hosts
	 * already hold these for their own dropdowns.
	 */
	projectId?: string;
	isPointsProject?: boolean;
	roles?: ReadonlyArray<{id: string; name?: string | null}>;
	assignablePersons?: ReadonlyArray<{id: string; firstName?: string | null; lastName?: string | null}>;
	phases?: ReadonlyArray<{id: string; name?: string | null}>;
	labels?: ReadonlyArray<{
		id: string;
		name?: string | null;
		color?: string | null;
		category?: {id: string; name?: string | null} | null;
	}>;
	showBillable?: boolean;
	/** Drives the revenue row in the edit form's Ends confirmation. */
	currencySymbol?: string | null;
	canEditTasks?: boolean;
	canDeleteTasks?: boolean;
	/**
	 * The company's minutes per weekday. The edit form needs it to predict what
	 * a later Ends date adds for a set that skips or moves: without it the form
	 * walks the raw pattern and can offer a date the backend creates nothing on.
	 */
	companyWorkingHours?: CompanyWorkingHours | null;
}

const drawerFragment = graphql`
	fragment ManageTaskSetsDrawer_project on ProjectType {
		taskSets(first: 1000) {
			edges {
				node {
					id
					name
					frequency
					intervalLength
					weekdays
					monthlyWeekday
					monthlyMode
					dayOfMonth
					weekOrdinal
					boundType
					boundCount
					boundEndDate
					nonWorkingDayAction
					materialisedTo
					# The instance the set ends after, which is what the Ends column
					# names -- the same sentence the edit form's Ends field shows.
					lastInstance {
						companyTaskId
						startDate
					}
					# Present only for a set whose generation stopped before the end it
					# was given. The list flags it under Ends; the edit form explains it.
					generationShortfall {
						requestedEndDate
						requestedCount
						createdCount
						remainingCount
						createdUpTo
					}
					seedDate
					instanceCount
					# How many of those instances have been hand-edited and so no
					# longer follow the set. Shown beside the total rather than in a
					# column of its own: the number only means something as a fraction
					# of the total it sits next to.
					exceptionCount
					# The stored defaults the edit form opens with. A set created
					# before these columns existed returns null for all of them, and
					# the form renders that as empty rather than borrowing an
					# instance's values.
					instanceName
					description
					estimateForecast
					billable
					role {
						id
					}
					phase {
						id
					}
					defaultLabels {
						id
					}
					defaultAssignedPersons {
						id
					}
					parentPhase {
						id
						name
					}
				}
			}
		}
	}
`;

/**
 * The task-set surface, mounted identically by the scoping page and the project
 * timeline.
 *
 * One TaskSetDrawer whose body swaps between the set list and one set's edit
 * form. Every entry point -- the Manage task sets button on either page, and the
 * set chip on either page -- opens the list, so "one surface reached several
 * ways" is literally true rather than a claim about two implementations that
 * happen to match.
 *
 * There is no read-only summary between the list and the form. The list already
 * carries what a summary would say, and a reader who opened a set wanted to
 * change it; a record page in between was a step that answered nothing.
 *
 * The inactive pane is HIDDEN, never unmounted, which is what lets the form
 * survive a trip back to the list with what the reader typed still in it.
 */
export const ManageTaskSetsDrawer = ({
	isOpen,
	project,
	highlightSetId = null,
	openNonce = 0,
	onClose,
	onDeleted,
	onEdited,
	projectId,
	isPointsProject,
	roles,
	assignablePersons,
	phases,
	labels,
	showBillable,
	currencySymbol,
	canEditTasks,
	canDeleteTasks,
	companyWorkingHours,
}: ManageTaskSetsDrawerProps) => {
	const intl = useIntl();
	const data = useFragment(drawerFragment, project as never) as {
		taskSets?: {edges?: ReadonlyArray<{node?: TaskSetRow | null} | null> | null} | null;
	} | null;

	const sets: TaskSetRow[] = (data?.taskSets?.edges || [])
		.map(edge => edge?.node)
		.filter((node): node is TaskSetRow => !!node);

	// Which pane is showing. Null is the list, which is where every open lands.
	const [openSetId, setOpenSetId] = useState<string | null>(null);
	// The set the form is BUILT from. It deliberately outlives openSetId: going
	// back hides the form, it does not destroy it.
	const [mountedSetId, setMountedSetId] = useState<string | null>(null);

	/* Bumped every time a set is opened, and part of the form's key, so opening
	   one always builds it from what is stored.

	   This is what stops an abandoned edit coming back. The form is kept mounted
	   while the list is showing, so without the bump a set reopened from the list
	   still held the values last typed into it -- and an unsaved change sitting in
	   a freshly opened form reads as a saved one. The cost is that leaving the
	   form for the list discards what was typed rather than holding it. */
	const [formEpoch, setFormEpoch] = useState(0);

	const showSet = (setId: string) => {
		setOpenSetId(setId);
		setMountedSetId(setId);
		setFormEpoch(epoch => epoch + 1);
	};

	const showList = () => setOpenSetId(null);

	/* The list row's delete. Deleting from the list leaves the list showing, so
	   there is nothing to navigate afterwards.

	   The refusal's way out -- "End the set early" -- opens the set, which now IS
	   the form holding the Ends field, so showing the set is the whole action.
	   Offered only to a reader who may edit: without that permission the form
	   never opens, and the button would refuse on the far side of a modal that
	   promised a way forward. */
	const {startDelete: startDeleteFromList} = useDeleteTaskSet(
		projectId,
		onDeleted,
		undefined,
		canEditTasks ? (setId: string) => showSet(setId) : undefined
	);

	/* Naming a set opens it; naming none opens the list. The chip is the only
	   caller that names one, and from an instance the set is already chosen.

	   Keyed on the nonce as well as the id, because asking twice in a row changes
	   no id and would otherwise fire nothing the second time. */
	useEffect(() => {
		if (highlightSetId) {
			showSet(highlightSetId);
		} else {
			showList();
		}
		// Deps are deliberately partial: showList is recreated every render and
		// listing it would re-run this on each one. No disable directive here --
		// web-front does not enable the react-hooks plugin, so naming one of its
		// rules in a disable comment is itself an eslint error.
	}, [highlightSetId, openNonce]);

	// Deliberately NO reset on close. TaskSetDrawer slides out over 250ms
	// (visibility 0s linear 250ms), so clearing openSetId here swapped the title
	// from 'About this task set' back to 'Manage task sets' and tore off the back
	// button and name chip while the drawer was still on screen -- the user watched
	// it revert to the list as it left. TimelineManageTaskSetsDrawer keeps itself
	// mounted via hasOpened for precisely this animation; discarding the pane
	// underneath it defeated that.
	//
	// Nothing needs the reset: every open request bumps openNonce, so the effect
	// above re-runs on each open and selects the pane afresh. Both callers
	// therefore leave the id alone on close -- nulling it there would re-fire that
	// effect and cause the same mid-slide flip by another route.

	const openSet = sets.find(set => set.id === openSetId) || null;
	const mountedSet = sets.find(set => set.id === mountedSetId) || null;

	return (
		<TaskSetDrawer
			isOpen={isOpen}
			title={
				openSet
					? intl.formatMessage({id: canEditTasks ? 'task_set.edit_set' : 'task_set.view_set'})
					: intl.formatMessage({id: 'task_set.manage'})
			}
			titleSize={openSet ? 17 : 20}
			titleAdornment={
				openSet ? (
					<>
						<BackButton
							type="button"
							onClick={showList}
							aria-label={intl.formatMessage({id: 'common.back'})}
							data-cy="task-set-drawer-back"
						>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
								<path d="M12 5l-5 5 5 5" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</BackButton>
						<TaskSetNameChip set={openSet} />
					</>
				) : null
			}
			onClose={onClose}
		>
			<div hidden={!!openSet} data-cy="task-set-list-pane">
				<TaskSetListPane
					sets={sets}
					highlightSetId={highlightSetId}
					readOnly={!canEditTasks}
					onViewSet={showSet}
					onDeleteSet={canDeleteTasks ? startDeleteFromList : undefined}
				/>
			</div>
			{/* Back is a pane switch, not a state reset -- it never touches
			    mountedSetId. Keyed on mountedSet, NOT openSet: once a set has been
			    opened the form stays in the DOM and merely hides on back, so it
			    keeps what the reader typed. Conditional rendering here would throw
			    that away on every trip to the list.

			    The key is what bounds that survival. The form seeds its fields from
			    `set` on mount only, so without one React reuses the instance and a
			    second set inherits the first one's values -- and an abandoned edit
			    comes back on reopen looking like it was saved. Keying on the set and
			    the open epoch rebuilds it every time a set is opened, from whichever
			    entry point, so what is on screen is always what is stored. */}
			{mountedSet && projectId ? (
				<div hidden={!openSet} data-cy="task-set-config-pane">
					<TaskSetEditForm
						key={`${mountedSet.id}-${formEpoch}`}
						set={mountedSet}
						projectId={projectId}
						isPointsProject={isPointsProject}
						roles={roles}
						assignablePersons={assignablePersons}
						phases={phases}
						labels={labels}
						showBillable={showBillable}
						currencySymbol={currencySymbol}
						readOnly={!canEditTasks}
						companyWorkingHours={companyWorkingHours}
						onCancel={showList}
						onSaved={instancesUpdated => {
							showList();
							onEdited?.(instancesUpdated);
						}}
					/>
				</div>
			) : null}
		</TaskSetDrawer>
	);
};
